import { useQueryClient } from '@tanstack/react-query';
import type { DashboardT, WidgetT } from 'business/definitions';
import { trackEvent } from 'helpers/trackerHelpers';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';
import type { WidgetPositionContextType } from 'lib/layout/context/WidgetPositionContext';
import { useExploreContext } from 'modules/decision/pages/Explore/components/Contexts/ExploreContext';
import type { SettingsState } from 'modules/decision/pages/Explore/hooks/reducers/types';
import { useTranslation } from 'react-i18next';
import DashboardSelectionModal from 'shared/components/ChartEditMenu/DashboardSelectionModal';
import { useAddChartToDashboard } from 'shared/components/ChartEditMenu/hooks/addChartToDashboard';
import { useDashboardWidgetContext } from 'shared/components/WidgetsLayout/context/WidgetContext';

type AddToDashboardModalProps = {
    show: boolean;
    closeModal: () => void;
    chartKey?: number | null;
    settings?: Pick<
        SettingsState,
        'chartTitle' | 'selectedViz' | 'chartSettings'
    >;
    isInDashboard?: boolean;
    query?: ExploreQueryT;
    widgetPosition?: WidgetPositionContextType | WidgetT['position'];
};

const AddToDashboardModal = ({
    show,
    chartKey = undefined,
    closeModal,
    settings = undefined,
    isInDashboard = undefined,
    query = undefined,
    widgetPosition = undefined,
}: AddToDashboardModalProps): JSX.Element => {
    const { t } = useTranslation();
    const { explore } = useExploreContext();
    const queryClient = useQueryClient();
    const currentDashboard = useGetDashboardFromSlugOrMetadata();
    const { key: widgetKey } = useDashboardWidgetContext();
    const { addChartToDashboard } = useAddChartToDashboard();

    const addChart = (dashboard: DashboardT, shouldInsertChartBelow: boolean) =>
        addChartToDashboard(
            explore,
            dashboard,
            chartKey,
            widgetKey,
            isInDashboard,
            query,
            settings,
            undefined,
            undefined,
            currentDashboard?.key === dashboard.key && shouldInsertChartBelow
                ? widgetPosition
                : undefined,
        );

    const hasChartKey = chartKey !== undefined && chartKey !== null;
    // IndicatorActions saves an indicator from its query, without chartKey
    const savesIndicator = hasChartKey || query !== undefined;

    const submit = (
        dashboards: DashboardT[],
        shouldInsertChartBelow: boolean,
    ) => {
        dashboards.forEach((dashboard) => {
            addChart(dashboard, shouldInsertChartBelow);
            if (savesIndicator) {
                trackEvent('decision/indicator/saved', { chartKey });
            }
            if (hasChartKey) {
                queryClient.invalidateQueries({
                    queryKey: ['widget', chartKey],
                });
            }
        });
    };

    const onCreateDashboard = (dashboard: DashboardT) => {
        addChart(dashboard, false);
        if (savesIndicator) {
            trackEvent('decision/indicator/saved', { chartKey });
        }
    };

    return (
        <DashboardSelectionModal
            show={show}
            closeModal={closeModal}
            insertBelowLabel={t('chart.insertBelowChart')}
            onSubmit={submit}
            onCreateDashboard={onCreateDashboard}
        />
    );
};

export default AddToDashboardModal;
